/* global __APP_BUILD_SIGNATURE__ */
import { tt } from "@/i18n";

const VERSION_STORAGE_KEY = "cloud_app_build_version";
const RELOAD_GUARD_KEY = "cloud_app_reload_guard";
const CHECK_INTERVAL_MS = 30 * 1000;
const RELOAD_GUARD_MS = 2 * 60 * 1000;

let checking = false;
let timer = null;
let updatePromptOpen = false;
let routerUnwatch = null;

function getVersionSignature(data) {
  if (!data || typeof data !== "object") {
    return "";
  }
  // 版本号 + 构建 hash，避免只改 package.json 或只改 hash 时检测不到
  const version = String(data.version || "").trim();
  const hash = String(data.hash || data.buildTime || "").trim();
  if (data.signature) {
    return String(data.signature);
  }
  if (version && hash) {
    return `${version}-${hash}`;
  }
  return hash || version;
}

function getRunningSignature() {
  if (typeof __APP_BUILD_SIGNATURE__ !== "undefined" && __APP_BUILD_SIGNATURE__) {
    return String(__APP_BUILD_SIGNATURE__);
  }
  if (typeof document === "undefined") {
    return "";
  }
  const meta = document.querySelector('meta[name="app-build"]');
  return meta ? String(meta.getAttribute("content") || "") : "";
}

function versionJsonUrl() {
  const base =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.BASE_URL) ||
    "/";
  const prefix = String(base).endsWith("/") ? base : `${base}/`;
  return `${prefix}version.json?_=${Date.now()}`;
}

function syncLocalVersion(signature) {
  if (signature) {
    localStorage.setItem(VERSION_STORAGE_KEY, signature);
  }
}

function isPublicEntryRoute() {
  if (typeof window === "undefined") {
    return false;
  }
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/" || path === "/login" || path === "/register";
}

function shouldSkipPromptAfterReload() {
  const guard = sessionStorage.getItem(RELOAD_GUARD_KEY);
  if (!guard) {
    return false;
  }
  const elapsed = Date.now() - Number(guard);
  if (Number.isNaN(elapsed) || elapsed > RELOAD_GUARD_MS) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
    return false;
  }
  return true;
}

async function promptForUpdate(signature) {
  if (updatePromptOpen || shouldSkipPromptAfterReload()) {
    return false;
  }
  updatePromptOpen = true;
  try {
    const { MessageBox } = await import("element-ui");
    await MessageBox.confirm(
      tt("检测到系统已发布新版本，请刷新页面以加载最新资源。"),
      tt("版本更新"),
      {
        confirmButtonText: tt("立即刷新"),
        cancelButtonText: tt("稍后"),
        type: "warning",
      }
    );
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    syncLocalVersion(signature);
    forceReloadApp();
    return true;
  } catch (e) {
    return false;
  } finally {
    updatePromptOpen = false;
  }
}

export async function fetchRemoteVersion() {
  const res = await fetch(versionJsonUrl(), {
    cache: "no-store",
    headers: { Pragma: "no-cache", "Cache-Control": "no-cache" },
  });
  if (!res.ok) {
    throw new Error("version fetch failed");
  }
  const contentType = String(res.headers.get("content-type") || "");
  const text = await res.text();
  const trimmed = (text || "").trim();
  if (
    !trimmed ||
    trimmed.charAt(0) === "<" ||
    (contentType && contentType.indexOf("html") !== -1)
  ) {
    throw new Error("version.json is not json");
  }
  return JSON.parse(trimmed);
}

export async function checkAppVersion(options = {}) {
  const { silent = false, forceNotify = false } = options;
  if (checking) {
    return false;
  }
  if (!forceNotify && isPublicEntryRoute()) {
    return false;
  }
  checking = true;
  try {
    const remote = await fetchRemoteVersion();
    const remoteSig = getVersionSignature(remote);
    if (!remoteSig) {
      return false;
    }

    const runningSig = getRunningSignature();
    const local = localStorage.getItem(VERSION_STORAGE_KEY);
    const currentSig = runningSig || local || "";

    if (currentSig && currentSig === remoteSig) {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      syncLocalVersion(remoteSig);
      if (forceNotify) {
        const { Message } = require("element-ui");
        Message.info("当前已是最新版本");
      }
      return false;
    }

    if (!currentSig) {
      syncLocalVersion(remoteSig);
      if (forceNotify) {
        const { Message } = require("element-ui");
        Message.info("当前已是最新版本");
      }
      return false;
    }

    if (shouldSkipPromptAfterReload()) {
      return false;
    }
    if (forceNotify || !silent) {
      await promptForUpdate(remoteSig);
    }
    return true;
  } catch (e) {
    if (forceNotify) {
      const { Message } = require("element-ui");
      Message.warning("无法读取版本信息，请确认已部署 version.json");
    }
    return false;
  } finally {
    checking = false;
  }
}

export function forceReloadApp() {
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(Date.now()));
  window.location.replace(url.toString());
}

export function startAppVersionWatcher(router) {
  if (timer) {
    return;
  }
  const runCheck = () => {
    checkAppVersion({ silent: false }).catch(() => {});
  };
  runCheck();
  timer = setInterval(runCheck, CHECK_INTERVAL_MS);
  window.addEventListener("focus", runCheck);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      runCheck();
    }
  });
  if (router && typeof router.afterEach === "function") {
    routerUnwatch = router.afterEach(() => {
      runCheck();
    });
  }
}

export function stopAppVersionWatcher() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (typeof routerUnwatch === "function") {
    routerUnwatch();
    routerUnwatch = null;
  }
}
